import EN from "./en.json";
import PATTERNS from "./patterns.json";
import { DATA_VALUES } from "./data";
import { http } from "../lib/api";

// Langue, pays et devise de la société (réglés par son administrateur), mis en cache local
const KEY = "xaltix_locale";
const SKIP = new Set(["SCRIPT", "STYLE", "TEXTAREA", "CODE", "PRE"]);
const ATTRS = ["placeholder", "title", "aria-label", "alt"];
const WS = /^(\s*)([\s\S]*?)(\s*)$/;
const NUM = /(\d+(?:[.,]\d+)*)/;
const dict = new Map(Object.entries(EN).map(([k, v]) => [k.replace(/\s+/g, " "), v]));
const lower = new Map([...dict].map(([k, v]) => [k.toLowerCase(), v]));
const SYM = { GBP: "£", CHF: "CHF", EUR: "€" };

const read = () => { try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch { return {}; } };
const L = read();
export const getLang = () => (L.lang === "en" ? "en" : "fr");
export const getCountry = () => L.cc || "FR";
export const getCurrency = () => L.cur || "EUR";
const LIC = { UK: { fr: "SIA", en: "SIA" }, CH: { fr: "cantonale", en: "cantonal" } };

const look = (s) => (DATA_VALUES.has(s) ? s : dict.get(s) ?? (s.length > 3 ? lower.get(s.toLowerCase()) : undefined));

const esc = (s) => s.replace(/[.*+?^$()|[\]\\]/g, "\\$&");
const PATS = Object.entries(PATTERNS).map(([fr, en]) => [new RegExp(`^${esc(fr).replace(/\\?\{(\d)\\?\}/g, "(.+?)").replace(/\{(\d)\}/g, "(.+?)")}$`), en]);
const pattern = (s) => {
  for (const [re, en] of PATS) {
    const m = s.match(re);
    if (m) return en.replace(/\{(\d)\}/g, (_, i) => { const v = m[+i + 1] || ""; return look(v) ?? v; });
  }
  return undefined;
};

// traduit le plus long début connu de la phrase, puis le reste
const prefix = (s, depth = 0) => {
  const t = look(s);
  if (t !== undefined) return t;
  const w = s.split(" ");
  if (depth > 3 || w.length < 2 || w.length > 40) return s;
  for (let i = w.length - 1; i >= 2; i--) {
    const h = look(w.slice(0, i).join(" "));
    if (h !== undefined) return `${h} ${prefix(w.slice(i).join(" "), depth + 1)}`;
  }
  return s;
};

const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const MONTHS_EN = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const MON_RE = new RegExp(`\\b(${MONTHS.join("|")})\\b`, "gi");
const months = (s) => s.replace(MON_RE, (m) => MONTHS_EN[MONTHS.indexOf(m.toLowerCase())]);

const DATA = /@|https?:|www\.|\.(fr|com|uk|ch)\b|^[A-Z]{2,}-\d/;

const core = (s) => {
  const t = look(s);
  if (t !== undefined) return t;
  if (DATA.test(s)) return s;
  const p = pattern(s);
  if (p !== undefined) return p;
  if (NUM.test(s)) {
    const parts = s.split(NUM);
    const out = parts.map((p, i) => (i % 2 ? p : p.replace(/^([\s()·:,–\-/]*)([\s\S]*?)([\s()·:,–\-/]*)$/, (_, a, b, c) => a + (b ? prefix(b) : b) + c)));
    if (out.some((p, i) => p !== parts[i])) return out.join("");
  }
  if (s.includes(" · ")) {
    const parts = s.split(" · ");
    const out = parts.map((p) => core(p.trim()));
    if (out.some((p, i) => p !== parts[i])) return out.join(" · ");
  }
  return prefix(s);
};

const local = (s) => {
  const cur = getCurrency(), lic = LIC[getCountry()];
  if (cur !== "EUR") s = s.replace(/€/g, SYM[cur]).replace(/\bEUR\b/g, cur);
  if (lic) s = s.replace(/\bCNAPS\b/g, lic[getLang()]);
  return s;
};

const miss = new Set();
const FRW = /[éèàêçùâîôûœ]|\b(le|la|les|des|du|une|et|pour|aucun|aucune|avec|sans|dans|vos|votre|vous|semaine|mois|sur|au|aux|est|sont|en)\b/i;
window.__i18nMiss = miss;

export const tr = (str) => {
  if (!str || typeof str !== "string") return str;
  const [, a, b, c] = str.match(WS);
  if (!b || !/[A-Za-zÀ-ÿ€]/.test(b)) return str;
  const en = getLang() === "en" ? months(core(b.replace(/\s+/g, " "))) : b;
  if (getLang() === "en" && !DATA_VALUES.has(b.trim()) && FRW.test(en) && miss.size < 3000) miss.add(b);
  return local(a + en + c);
};

const seen = new WeakMap();
const skip = (el) => !el || el.closest?.("[data-no-i18n],[contenteditable=true]");

const doText = (n) => {
  if (seen.get(n) === n.nodeValue) return;
  const p = n.parentElement;
  if (p && !SKIP.has(p.tagName) && !skip(p)) {
    const t = tr(n.nodeValue);
    if (t !== n.nodeValue) n.nodeValue = t;
  }
  seen.set(n, n.nodeValue);
};

const doEl = (el) => {
  if (skip(el)) return;
  for (const a of ATTRS) {
    const v = el.getAttribute(a);
    if (v) { const t = tr(v); if (t !== v) el.setAttribute(a, t); }
  }
};

const walk = (root) => {
  if (root.nodeType === 3) return doText(root);
  if (root.nodeType !== 1 || SKIP.has(root.tagName)) return;
  doEl(root);
  const w = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  for (let n = w.nextNode(); n; n = w.nextNode()) (n.nodeType === 3 ? doText(n) : doEl(n));
};

const patchFormats = () => {
  const en = getLang() === "en", cur = getCurrency();
  const fl = (l, o) => (o && o.style === "currency" && cur !== "EUR" ? (cur === "GBP" ? "en-GB" : "de-CH") : en && typeof l === "string" && l.startsWith("fr") ? "en-GB" : l);
  const fo = (o) => (o && o.currency === "EUR" && cur !== "EUR" ? { ...o, currency: cur } : o);
  for (const [P, ms] of [[Date.prototype, ["toLocaleDateString", "toLocaleString", "toLocaleTimeString"]], [Number.prototype, ["toLocaleString"]]]) {
    for (const m of ms) { const f = P[m]; P[m] = function (l, o) { return f.call(this, fl(l, o), fo(o)); }; }
  }
  const DTF = Intl.DateTimeFormat, NF = Intl.NumberFormat;
  Intl.DateTimeFormat = function (l, o) { return new DTF(fl(l), o); };
  Intl.DateTimeFormat.supportedLocalesOf = DTF.supportedLocalesOf;
  Intl.NumberFormat = function (l, o) { return new NF(fl(l, o), fo(o)); };
  Intl.NumberFormat.supportedLocalesOf = NF.supportedLocalesOf;
};

export function startI18n() {
  document.documentElement.lang = getLang() === "en" ? "en-GB" : "fr";
  if (getLang() !== "en" && getCurrency() === "EUR" && getCountry() === "FR") return;
  patchFormats();
  const { confirm, alert, prompt } = window;
  window.confirm = (m) => confirm(tr(m));
  window.alert = (m) => alert(tr(m));
  window.prompt = (m, d) => prompt(tr(m), d);
  const go = () => {
    walk(document.body);
    new MutationObserver((ms) => {
      for (const m of ms) {
        if (m.type === "characterData") doText(m.target);
        else if (m.type === "attributes") doEl(m.target);
        else m.addedNodes.forEach(walk);
      }
    }).observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ATTRS });
  };
  document.body ? go() : document.addEventListener("DOMContentLoaded", go);
}

// aligne le cache local sur la langue / le pays de la société ; recharge si changement
export function applyLocale(b) {
  if (!b || !b.default_lang) return;
  const next = { lang: b.default_lang, cc: b.country_code || "FR", cur: b.currency || "EUR" };
  if (next.lang !== getLang() || next.cc !== getCountry() || next.cur !== getCurrency()) {
    localStorage.setItem(KEY, JSON.stringify(next));
    window.location.reload();
  }
}

export const syncLocale = () => http.get("/brand").then((r) => applyLocale(r.data)).catch(() => {});
